import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { CameraIconBold, SelfieIcon, TickIcon, TickRedWhiteCircle } from 'src/assets/images';

import { SalesforceContext } from 'src/context/salesforce-context';
import ImageCaptureModal from 'src/components/image-capture-modal/image-capture-modal';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { InfoIcon } from 'src/assets/images';
import { ErrorCodeTooltip } from 'src/features/common/components/error-code-tooltip/error-code-tooltip';
import TealiumEvents from 'src/helpers/tealium-events';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { Info } from 'src/components';
import { Prompt } from 'src/features/common/components/prompt/prompt';
import { Steps } from 'src/context/salesforce-context';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import './your-details-selfie.scss';

export default function YourDetailsSelfie(props) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const { merchant, setMerchant, customerType, lead } = useContext(SalesforceContext);
  const outsideRef = useRef(null);
  const handleTechnicalError = useTechnicalErrorHandler();
  const isPending = useMemo(() => !!merchant.isPendingApp, [merchant.isPendingApp]);
  const hints = useMemo(() => {
    const selfie = 'selfie';
    return (
      merchant.pageUpdates
        ?.find(
          (pageUpdate) =>
            pageUpdate.subStepName.toLowerCase() === Steps.YOUR_DETAILS_SELFIE.toLowerCase(),
        )
        ?.fields.find(({ fieldName }) => fieldName === selfie)?.hints || []
    );
  }, [merchant.pageUpdates]);

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.selfiePageLoad(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
    }
  }, [merchant?.account?.business_type]);

  useEffect(() => {
    if (merchant?.contact?.contact_photo) {
      setImgSrc(`data:image/jpeg;base64,${merchant.contact.contact_photo}`);
    }

    props.toggleNext(false);
  }, []);

  useEffect(() => {
    setContactImage();
    if (imgSrc) {
      props.toggleNext(true);
    }
  }, [imgSrc]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (outsideRef.current && !outsideRef.current.contains(event.target)) {
        setShowWebcamModal(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [outsideRef]);

  const tooltipText =
    'Your selfie helps us protect you from identity theft. ' +
    'We use it to verify your identity against the Department of Home Affairs records.';

  const textTable = {
    dealer: {
      subtitle:
        'To proceed with the application, we need to verify the identity of the customer with a photo',
    },
    agent: {
      subtitle:
        'To proceed with the application, we need to verify the identity of the customer with a photo',
    },
    normal: {
      subtitle:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'To proceed with the application, we need to verify your identity with a photo of yourself'
          : 'To proceed with the application, we need to verify the identity of the customer with a photo',
    },
    pos: {
      subtitle:
        'To proceed with the application, we need to verify your identity with a photo of yourself',
    },
  };

  const getBase64DataOnly = (urlEncodedString) =>
    urlEncodedString ? urlEncodedString.split(',')[1] : '';

  const setContactImage = () => {
    setMerchant({
      ...merchant,
      contact: {
        ...merchant.contact,
        contact_photo: getBase64DataOnly(imgSrc),
        photo_file_name: 'selfie.jpg',
      },
    });
  };

  const getFileType = (base64Data) => {
    const firstChar = base64Data.charAt(0);
    switch (firstChar) {
      case '/':
        return { ext: '.jpg', mime: 'image/jpeg' };
      case 'i':
        return { ext: '.png', mime: 'image/png' };
      case 'R':
        return { ext: '.gif', mime: 'image/gif' };
    }

    ///Assume PNG by default as this is what is sent by mini-app
    return { ext: '.png', mime: 'image/png' };
  };

  const openWebcam = () => {
    if (!imgSrc) {
      TealiumEvents.selfieOpenCam(customerType);
    } else {
      TealiumEvents.selfieRetake(customerType);
    }
    //If in mini program
    if (inMiniProgram) {
      MiniAppEvents.listenForMessage((observer) => {
        const message = MiniAppEvents.readMessage();
        if (message.type === 'selfie') {
          observer.disconnect();
          if (message.error) {
            //If error occurs while trying to upload file on mini-app side
            TealiumEvents.dxlErrorUploadMiniApp(message.errorMessage, 'selfie options');
            handleTechnicalError(message.errorMessage, true, true);
          } else if (message.url) {
            //If message returned is selfie message, then set imgSrc
            MiniAppEvents.downloadBase64FileURL(message.url)
              .then((res) => {
                resizeDataURL(res, false, 1.2);
              })
              .catch((error) => {
                //If error occurs while trying to download file on portal side
                TealiumEvents.dxlErrorDownloadMiniApp(error, 'selfie options');
                handleTechnicalError(error, true, true);
              });
          }
        }
      });
      //Tell mini app we want to take a selfie
      MiniAppEvents.sendMessage({
        messageType: 'selfie',
        prefix: 'data:image/jpeg;base64,',
      });
    } else {
      // If not in mini program
      setShowWebcamModal(true);
    }
  };

  const resizeDataURL = (data, preserveWidth, scalingFactor) => {
    const height = 480;
    const width = 640;
    // We create an image to receive the Data URI
    var img = document.createElement('img');

    // When the event "onload" is triggered we can resize the image.
    img.onload = function () {
      // check scale
      var scale = preserveWidth
        ? (width / img.width) * scalingFactor
        : (height / img.height) * scalingFactor;

      // We create a canvas and get its context.
      var canvas = document.createElement('canvas');
      var ctx = canvas.getContext('2d');

      canvas.width = width;
      canvas.height = height;
      ctx.scale(scale, scale);

      const xPos = ((canvas.width - img.width * scale) * 0.5) / scale;
      const yPos = ((canvas.height - img.height * scale) * 0.5) / scale;

      // Center the image within canvas bounds (crop to center)
      ctx.drawImage(img, xPos, yPos);

      //Convert canvas image to JPEG
      var dataURI = canvas.toDataURL('image/jpeg');
      TealiumEvents.selfieTakePhoto(customerType, merchant.isPendingApp);
      setImgSrc(dataURI);
    };

    // We put the Data URI in the image's src attribute (done after onload is declared to ensure it is defined on-load)

    const fileType = getFileType(data);
    img.src = `data:${fileType.mime};base64,${data}`;
  };

  return (
    <div className="YourDetailsSelfie" data-testid="YourDetailsSelfie">
      {!inMiniProgram && (
        <ImageCaptureModal
          show={showWebcamModal}
          onHide={() => setShowWebcamModal(false)}
          onImageCapture={(img) => {
            TealiumEvents.selfieTakePhoto(customerType, merchant.isPendingApp);
            setImgSrc(img);
          }}
          refreshTagging={() => TealiumEvents.selfieRefreshOrSwitch(customerType, false)}
          switchTagging={() => TealiumEvents.selfieRefreshOrSwitch(customerType, true)}
          customerType={customerType}
          automationIdPrefix="Onboarding_Create_Account_Your_Details"
        />
      )}
      <div className="columns">
        <div className="column is-10 is-offset-1">
          <div className="selfie-container">
            <span className="is-flex is-flex-direction-row is-align-items-center">
              <h1 className="has-padding-top-3 step-main-heading">
                Let's take a selfie
                <Info
                  className="selfie-tooltip"
                  text={tooltipText}
                  orientation="bottom"
                  onOpenEvent={() => TealiumEvents.selfieInfo(customerType)}
                />
              </h1>
            </span>

            <p className=" has-padding-top-5 step-sub-heading">
              {isPending ? 'Please retake your selfie.' : textTable[customerType].subtitle}
            </p>
            {isPending && (
              <ErrorCodeTooltip lightMode={false} content={hints}>
                <Prompt center icon={InfoIcon} />
              </ErrorCodeTooltip>
            )}

            <div className="columns body-section">
              <div className="column is-two-fifths selfie-image-section">
                {!imgSrc && <img className="has-padding-top-3 selfie-icon" src={SelfieIcon} />}

                {imgSrc && (
                  <div className="image-section">
                    <div className="image-container-wrapper">
                      <img className="selfie-tick-icon" src={TickIcon} />

                      <div className="image-container">
                        <img
                          className="selfie-image"
                          src={imgSrc}
                          automationid="Onboarding_Create_Account_Your_Details_Img_Selfie_Image"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="column is-three-fifths">
                <div className="is-hidden-mobile has-padding-left-4">
                  <div className="content-large-regular tips-heading">
                    Tips for taking your selfie:
                  </div>

                  <div className="tips">
                    <div className="row">
                      <img src={TickRedWhiteCircle} />
                      <p>
                        Dark glasses or non-prescription
                        <br />
                        glasses not allowed
                      </p>
                    </div>

                    <div className="row">
                      <img src={TickRedWhiteCircle} />

                      <p>
                        Don’t wear a hat or head covering
                        <br />
                        that obscures your hair or hairline
                      </p>
                    </div>

                    <div className="row">
                      <img src={TickRedWhiteCircle} />
                      <p>Ensure that it’s a clear headshot</p>
                    </div>

                    <div className="row">
                      <img src={TickRedWhiteCircle} />
                      <p>Avoid having family/friends in the photo</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button className="button open-webcam-button" onClick={openWebcam}>
              <img className="camera-icon" src={CameraIconBold} />{' '}
              <span className="open-webcam-button-text content-large-extra is-hidden-mobile is-hidden-tablet-only">
                {!imgSrc && !isPending ? 'Open webcam ' : 'Retake photo'}
              </span>
              <span className="open-webcam-button-text content-large-extra is-hidden-desktop-only is-hidden-widescreen-only is-hidden-fullhd">
                {!imgSrc && !isPending ? 'Open camera ' : 'Retake photo'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
